import {graphql} from 'gatsby';
import React, {FC} from 'react';

import {Footer as FooterComponent, FooterProps} from '../../components/modules/navigation/footer/footer';
import {Footer as FooterComponentR4} from '../../components/modules/navigation/footerR4/footer';
import {
  Footer as FooterComponentFrontPageR4,
  FooterProps as FooterPropsR4FrontPage
} from '../../components/modules/navigation/footerR4FrontPage/footer';
import {FooterFragment, Maybe} from '../../lib/queryTypes';
import {notEmpty} from './utils';
import {SocialMediaTypes} from '../../components/modules/socialMedia/widget';
import {DesignVersionType} from '../../lib/styleHelpers';

type FooterDynamicProps = {
  data: Maybe<FooterFragment>;
  isDark?: boolean;
  designVersion?: DesignVersionType;
};

type FooterDynamicPropsR4FrontPage = {
  data: Maybe<FooterFragment>;
};

const transformProduct = (product?: Maybe<{title: Maybe<string>; text: Maybe<string>}>) => ({
  title: product?.title ?? '',
  text: product?.text ?? ''
});

const transformProps = (dynamicProps: FooterDynamicProps): FooterProps => {
  const {data} = dynamicProps;

  return {
    copyrightText: data?.data?.copyright ?? '',
    socialMediaLinks: (data?.data?.socials ?? []).filter(notEmpty).map(social => ({
      type: social.type as SocialMediaTypes,
      title: social.title ?? '',
      href: social.href ?? ''
    })),
    bottomLinks: (data?.data?.bottomLinks ?? []).filter(notEmpty).map(link => ({
      title: link.title ?? '',
      href: link.href ?? ''
    })),
    content: (data?.data?.content ?? []).filter(notEmpty).map(c => ({
      title: c.title ?? '',
      links:
        c.links?.filter(notEmpty).map(link => ({
          title: link.title ?? '',
          href: link.href ?? '',
          highlightedText: link.highlightedText || '',
          isBold: link.isBold || false
        })) ?? []
    }))
  };
};

const transformFrontPageProps = (dynamicProps: FooterDynamicPropsR4FrontPage): FooterPropsR4FrontPage => {
  const {data} = dynamicProps;

  return {
    product: transformProduct(data?.data?.product),
    copyrightText: data?.data?.copyright ?? '',
    socialMediaLinks: (data?.data?.socials ?? []).filter(notEmpty).map(social => ({
      type: social.type as SocialMediaTypes,
      title: social.title ?? '',
      href: social.href ?? ''
    })),
    bottomLinks: (data?.data?.bottomLinks ?? []).filter(notEmpty).map(link => ({
      title: link.title ?? '',
      href: link.href ?? ''
    })),
    content: (data?.data?.content ?? []).filter(notEmpty).map(c => ({
      title: c.title ?? '',
      links:
        c.links?.filter(notEmpty).map(link => ({
          title: link.title ?? '',
          href: link.href ?? ''
        })) ?? []
    }))
  };
};

export const Footer: FC<FooterDynamicProps> = props => {
  const designVersion = props.data?.data?.designVersion;

  if (designVersion === 'r4')
    return <FooterComponentR4 isDark={props.isDark} {...transformProps(props)} />;
  if (designVersion === 'r4-frontPage')
    return <FooterComponentFrontPageR4 {...transformFrontPageProps(props)} />;

  return <FooterComponent {...transformProps(props)} />;
};

export const query = graphql`
  fragment Footer on ContentfulCommonJsonData {
    data {
      bottomLinks {
        href
        title
      }
      content {
        links {
          href
          title
          highlightedText
          isBold
        }
        title
      }
      product {
        title
        text
      }
      socials {
        href
        title
        type
      }
      copyright
      designVersion
    }
  }
`;
